import { useFormikContext } from 'formik'
import React, { useCallback, useState } from 'react'
import toastr from 'toastr'
import cloudinaryTools from '../../../../util/cloudinaryTools'
import ResponsiveImage from '../../misc/MediaPresets'
import { BuildingIcon, CameraIcon, TrashIcon } from './OrganizationIcons'
import {
  StyledLabel,
  StyledLogoActions,
  StyledLogoPreview,
  StyledLogoUploadContainer,
  StyledLogoUploadContent,
  StyledPlaceholderLogo,
  StyledRemoveLogoButton,
  StyledUploadLabel,
} from './OrganizationDetail.styles'

type LogoUploadFieldProps = {
  currentLogoPublicId: string | null
  organizationId: string
}

const LogoUploadField: React.FC<LogoUploadFieldProps> = ({ currentLogoPublicId, organizationId }) => {
  const { values, setFieldValue } = useFormikContext<{ logoPublicId: string }>()
  const [uploading, setUploading] = useState(false)

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        toastr.error('Please select a valid image file.')
        return
      }

      setUploading(true)

      await cloudinaryTools.upload(file, { folder: 'logo', organizationId }, (error, response) => {
        setUploading(false)

        if (error) {
          toastr.error(error)
        } else {
          setFieldValue('logoPublicId', (response as { public_id: string }).public_id)
        }
      })
    },
    [setFieldValue, organizationId],
  )

  const handleRemoveLogo = useCallback(() => {
    setFieldValue('logoPublicId', '')
  }, [setFieldValue])

  const logoPublicId = values.logoPublicId || currentLogoPublicId

  return (
    <StyledLogoUploadContainer>
      <StyledLabel>Organization Logo</StyledLabel>
      <StyledLogoUploadContent>
        <StyledLogoPreview>
          {logoPublicId ? (
            <ResponsiveImage publicId={logoPublicId} width="80" height="80" />
          ) : (
            <StyledPlaceholderLogo>
              <BuildingIcon />
            </StyledPlaceholderLogo>
          )}
        </StyledLogoPreview>
        <StyledLogoActions>
          <StyledUploadLabel htmlFor="logo-upload" isUploading={uploading}>
            <CameraIcon />
            {uploading ? 'UPLOADING...' : 'UPLOAD LOGO'}
            <input id="logo-upload" type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
          </StyledUploadLabel>
          {logoPublicId && (
            <StyledRemoveLogoButton type="button" onClick={handleRemoveLogo}>
              <TrashIcon />
              REMOVE
            </StyledRemoveLogoButton>
          )}
        </StyledLogoActions>
      </StyledLogoUploadContent>
    </StyledLogoUploadContainer>
  )
}

export default LogoUploadField
